import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarSync, RefreshCw, Trash2, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";

/**
 * CalendarSubscriptions — paste a Google Calendar (or any standard
 * iCal) "secret URL" and FamFeel mirrors its events into the family
 * calendar. Read-only: this is a one-way pull, kept simple on purpose.
 *
 * Imported events:
 *   • are tagged with no attendees — the parent is nudged to assign
 *     "Who it's for" themselves (an ICS feed has no idea who in the
 *     family this Wednesday's yoga class is for)
 *   • get a soft "from Google" badge wherever they render
 *   • are deduped by stable ICS UID, so re-syncs update instead of
 *     duplicating
 *   • skip member-birthday duplicates — if the imported title is a
 *     family member's name + "birthday" near their own birthday,
 *     FamFeel keeps its own synthesized birthday event instead.
 */
export default function CalendarSubscriptions() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  // When checked, every event imported from the new subscription
  // renders as a faint background band on the month grid instead
  // of a full event pill — designed for school-holiday feeds and
  // public-holiday calendars that would otherwise blanket the
  // grid with all-day stripes and bury actual family events.
  const [asBand, setAsBand] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const list = await api.listCalendarSubscriptions();
      setSubs(Array.isArray(list) ? list : []);
    } catch {
      setSubs([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    reload();
  }, []);

  const add = async () => {
    const v = url.trim();
    if (!/^https?:\/\//i.test(v)) {
      toast.error("Paste the full URL — it should start with https://");
      return;
    }
    setBusy(true);
    try {
      const created = await api.createCalendarSubscription({
        url: v,
        label: label.trim() || null,
        display_as_band: asBand,
      });
      toast.success(
        asBand
          ? "Holiday feed connected — events will render as quiet bands."
          : "Calendar connected. Pulling events…"
      );
      setUrl("");
      setLabel("");
      setAsBand(false);
      await reload();
      // The backend kicks off a sync immediately on create, but the
      // first few seconds will still show "Last synced never" because
      // the row was inserted before the async sync finished. Re-poll
      // after a short delay so the card lights up without the user
      // having to refresh or hit "Sync now".
      const subId = created?.id;
      if (subId) {
        for (const delay of [2500, 6000, 12000]) {
          setTimeout(async () => {
            try {
              const list = await api.listCalendarSubscriptions();
              if (Array.isArray(list)) setSubs(list);
            } catch {/* non-fatal */}
          }, delay);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't connect");
    } finally {
      setBusy(false);
    }
  };

  const sync = async (id) => {
    setBusy(true);
    try {
      const updated = await api.syncCalendarSubscription(id);
      setSubs((prev) => prev.map((s) => (s.id === id ? updated : s)));
      if (updated.last_error) {
        toast.error(updated.last_error);
      } else {
        toast.success(`Synced — ${updated.last_event_count || 0} events`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Disconnect this calendar? Imported events from "${s.label || s.url_preview}" will be removed.`)) return;
    setBusy(true);
    try {
      await api.deleteCalendarSubscription(s.id);
      setSubs((prev) => prev.filter((x) => x.id !== s.id));
      toast.success("Calendar disconnected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't disconnect");
    } finally {
      setBusy(false);
    }
  };

  const fmtRel = (iso) => {
    if (!iso) return "never";
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.round(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
  };

  return (
    <div className="space-y-4" data-testid="calendar-subscriptions">
      <div className="flex items-baseline gap-2">
        <CalendarSync className="w-4 h-4" strokeWidth={2} />
        <h3 className="font-display-italic text-xl leading-none tracking-tight">
          Pull events from another calendar
        </h3>
      </div>
      <p className="text-[13px] text-[color:var(--ink-2)] leading-snug">
        Paste a Google Calendar (or iCloud / Outlook) <strong>secret iCal URL</strong> —
        FamFeel will mirror its events into the family calendar every few hours.
        Read-only, no sign-in needed. After import, open each event to tag who it's for.
      </p>

      <button
        type="button"
        onClick={() => setShowHelp((v) => !v)}
        className="text-[12px] underline text-[color:var(--ink-2)] inline-flex items-center gap-1"
        data-testid="calendar-help-toggle"
      >
        Where do I find that URL?
        {showHelp ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {showHelp && (
        <div className="rounded-xl bg-[color:var(--bg-soft)] p-4 text-[13px] leading-snug space-y-3">
          <div>
            <div className="font-medium mb-1">Google Calendar</div>
            <ol className="list-decimal pl-5 space-y-0.5 text-[color:var(--ink-2)]">
              <li>Open <code>calendar.google.com</code> in a browser (works on phone too)</li>
              <li>Top-right ⚙️ → <em>Settings</em></li>
              <li>Under <em>Settings for my calendars</em> on the left, tap the calendar you want to share</li>
              <li>Tap <em>Integrate calendar</em></li>
              <li>
                Scroll to <strong>Secret address in iCal format</strong>
                <span className="text-[color:var(--ink-3)]"> (the one ending in <code>/private-…/basic.ics</code>, not the public one)</span>
              </li>
              <li>Tap the eye icon to reveal it, then the copy icon next to it</li>
              <li>Paste it below</li>
            </ol>
          </div>

          <div>
            <div className="font-medium mb-1">Apple iCloud Calendar</div>
            <ol className="list-decimal pl-5 space-y-0.5 text-[color:var(--ink-2)]">
              <li>Open <code>icloud.com/calendar</code> on a computer (it can't be done from the iPhone Calendar app)</li>
              <li>Hover the calendar in the left sidebar → tap the <em>broadcast / share</em> icon</li>
              <li>Tick <strong>Public Calendar</strong> and copy the <code>webcal://…</code> URL</li>
              <li>Paste it below — FamFeel converts <code>webcal://</code> to <code>https://</code> automatically</li>
            </ol>
            <p className="text-[color:var(--ink-3)] mt-1">
              iCloud only offers a public URL (no separate secret one), so anyone with it can read the calendar. Use a calendar you don't mind sharing.
            </p>
          </div>

          <div>
            <div className="font-medium mb-1">Outlook.com / Microsoft 365</div>
            <ol className="list-decimal pl-5 space-y-0.5 text-[color:var(--ink-2)]">
              <li>Open <code>outlook.live.com/calendar</code></li>
              <li>⚙️ <em>Settings</em> → <em>Calendar</em> → <em>Shared calendars</em></li>
              <li>Under <em>Publish a calendar</em>, pick the calendar and choose <strong>Can view all details</strong></li>
              <li>Tap <em>Publish</em>, then copy the <strong>ICS</strong> link (not the HTML one)</li>
              <li>Paste it below</li>
            </ol>
          </div>

          <p className="text-[color:var(--ink-3)] border-t border-[color:var(--line-soft)] pt-2">
            Treat the secret URL like a password — anyone with it can read every
            event on that calendar. FamFeel never shares it back out, but a leak
            on your end exposes your calendar. You can revoke a Google secret
            URL any time by tapping <em>Reset</em> next to it (this also
            disconnects FamFeel until you paste the new URL).
          </p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Label (optional)"
          maxLength={40}
          className="ff-input sm:max-w-[180px]"
          data-testid="calendar-sub-label"
        />
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://calendar.google.com/calendar/ical/.../private-…/basic.ics"
          className="ff-input flex-1"
          data-testid="calendar-sub-url"
        />
        <button
          type="button"
          onClick={add}
          disabled={busy || !url.trim()}
          className="ff-btn ff-btn-primary"
          data-testid="calendar-sub-add"
        >
          {busy ? "Connecting…" : "Connect"}
        </button>
      </div>
      <label
        className="flex items-start gap-2 text-[12px] text-[color:var(--ink-2)] leading-snug cursor-pointer"
        data-testid="calendar-sub-as-band"
      >
        <input
          type="checkbox"
          checked={asBand}
          onChange={(e) => setAsBand(e.target.checked)}
          className="mt-0.5 cursor-pointer"
        />
        <span>
          Show events from this calendar as <strong>quiet background bands</strong>
          {" "}— ideal for school holidays or public-holiday feeds. Won't crowd
          the month grid; won't trigger pings.
        </span>
      </label>

      {!loading && subs.length === 0 && (
        <div className="text-[13px] text-[color:var(--ink-3)] py-2">
          No calendars connected yet.
        </div>
      )}

      <ul className="space-y-2">
        {subs.map((s) => (
          <li
            key={s.id}
            className="rounded-xl border border-[color:var(--line-soft)] bg-[color:var(--surface)] p-3 flex items-start gap-3 flex-wrap"
            data-testid={`calendar-sub-${s.id}`}
          >
            <ExternalLink className="w-4 h-4 mt-0.5 flex-shrink-0 text-[color:var(--ink-3)]" strokeWidth={2} />
            <div className="flex-1 min-w-0">
              <div className="font-display tracking-tight text-base flex items-center gap-2 flex-wrap">
                {s.label || "Calendar"}
                {s.disabled && (
                  <span
                    className="rounded-full bg-[color:var(--rose)]/15 text-[color:var(--rose)] label-tiny px-2 py-0.5"
                    data-testid={`calendar-sub-disabled-${s.id}`}
                  >
                    Paused
                  </span>
                )}
              </div>
              <div className="label-tiny text-[color:var(--ink-3)] truncate">
                {s.url_preview}
              </div>
              <div className="label-tiny text-[color:var(--ink-3)] mt-1">
                {s.disabled ? (
                  <span className="text-[color:var(--rose)]">
                    Stopped after {s.consecutive_failures || 10} failed tries
                    — reconnect or remove. {s.last_error ? `Last error: ${s.last_error}` : ""}
                  </span>
                ) : s.last_error ? (
                  <span className="text-[color:var(--rose)]">{s.last_error}</span>
                ) : (
                  <>
                    Last synced {fmtRel(s.last_synced_at)}
                    {typeof s.last_event_count === "number" && (
                      <> · {s.last_event_count} events</>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => sync(s.id)}
                disabled={busy}
                className="p-2 rounded-full hover:bg-[color:var(--bg-soft)]"
                title="Sync now"
                aria-label="Sync now"
                data-testid={`calendar-sub-sync-${s.id}`}
              >
                <RefreshCw className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={() => remove(s)}
                disabled={busy}
                className="p-2 rounded-full hover:bg-[color:var(--bg-soft)] text-[color:var(--ink-3)]"
                title="Disconnect"
                aria-label="Disconnect"
                data-testid={`calendar-sub-delete-${s.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
